import React, { useState, useEffect } from 'react';
import {
  TextField,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Divider,
  InputAdornment,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import ColorSelector from '../ColorSelector';
import CharacterOptions from '../CharacterOptions';
import { GGDialog, GGDialogTitle } from './StyledDialogs';
import { MenuHeader } from './StyledComponents';
import { STATUS_ICONS } from '../../constants/gameConstants';
import { styled } from '@mui/material/styles';

// Health Stat Card Component
const HealthStatCard = ({ label, value }) => (
  <Card 
    variant="outlined" 
    sx={{ 
      flex: 1,
      textAlign: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
    }}
  >
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 600,
          mb: 0.5,
          color: 'primary.main',
        }}
      >
        {value || 0}
      </Typography>
      <Typography 
        variant="caption" 
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {label}
      </Typography>
    </CardContent>
  </Card>
);

// Edit Character Dialog
export const EditCharacterDialog = ({
  open,
  onClose,
  coin,
  onSave,
  canManageEnemies = false,
}) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [isLarge, setIsLarge] = useState(false);
  const [isEnemy, setIsEnemy] = useState(false);
  const [armorClass, setArmorClass] = useState('');
  const [level, setLevel] = useState('');
  const [isPc, setIsPc] = useState(true);

  useEffect(() => {
    if (coin) {
      setName(coin.name || '');
      setColor(coin.color || '');
      setIsLarge(coin.isLarge || false);
      setIsEnemy(coin.isEnemy || false);
      setArmorClass(coin.armorClass?.toString() || '');
      setLevel(coin.level != null ? String(coin.level) : '');
      setIsPc(coin.isPc !== false);
    }
  }, [coin]);

  const handleSave = () => {
    const levelNum =
      isEnemy || level === ''
        ? null
        : Math.min(20, Math.max(1, parseInt(level, 10) || 1));
    onSave?.(
      name,
      color,
      isLarge,
      isEnemy,
      armorClass ? parseInt(armorClass, 10) : null,
      isEnemy ? null : levelNum,
      isEnemy ? undefined : isPc
    );
    onClose();
  };

  return (
    <GGDialog open={open} onClose={onClose}>
      <GGDialogTitle>Edit Character</GGDialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Character Name"
          fullWidth
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <TextField
          margin="dense"
          label="Armor Class (Optional)"
          fullWidth
          variant="outlined"
          type="number"
          value={armorClass}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || /^\d+$/.test(value)) {
              setArmorClass(value);
            }
          }}
        />

        {!isEnemy && (
          <TextField
            margin="dense"
            label="Level (Optional)"
            fullWidth
            variant="outlined"
            type="number"
            value={level}
            inputProps={{ min: 1, max: 20 }}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                const n = parseInt(value, 10);
                if (value === '' || (n >= 1 && n <= 20)) {
                  setLevel(value);
                }
              }
            }}
            helperText="1–20, used for live encounter difficulty (player tokens)"
          />
        )}

        <CharacterOptions
          canManageEnemies={canManageEnemies}
          isEnemy={isEnemy}
          setIsEnemy={setIsEnemy}
          isLarge={isLarge}
          setIsLarge={setIsLarge}
          isPc={isPc}
          setIsPc={setIsPc}
        />
        
        <ColorSelector 
          selectedColor={color}
          setSelectedColor={setColor}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="outlined">Save</Button>
      </DialogActions>
    </GGDialog>
  );
};

// Edit Health Dialog
export const EditHealthDialog = ({
  open,
  onClose,
  coin,
  onSave,
}) => {
  const [healAmount, setHealAmount] = useState('');
  const [damageAmount, setDamageAmount] = useState('');
  const [finalHealth, setFinalHealth] = useState('');

  useEffect(() => {
    if (coin) {
      setFinalHealth((coin.currentHealth ?? coin.maxHealth)?.toString() || '');
      setHealAmount('');
      setDamageAmount('');
    }
  }, [coin]);

  const calculateFinalHealth = (heal, damage) => {
    const currentHealth = parseInt(coin?.currentHealth ?? coin?.maxHealth) || 0;
    const healNum = parseInt(heal) || 0;
    const damageNum = parseInt(damage) || 0;
    return Math.max(0, currentHealth + healNum - damageNum).toString();
  };

  const handleHealthInputChange = (value, setter) => {
    if (value === '' || /^\d+$/.test(value)) {
      if (setter === setFinalHealth) {
        setter(value);
        setHealAmount('');
        setDamageAmount('');
      } else {
        setter(value);
        const newHeal = setter === setHealAmount ? value : healAmount;
        const newDamage = setter === setDamageAmount ? value : damageAmount;
        setFinalHealth(calculateFinalHealth(newHeal, newDamage));
      }
    }
  };

  const handleKill = () => {
    onSave?.(0);
    onClose();
  };

  const handleSave = () => {
    if (finalHealth) {
      onSave?.(parseInt(finalHealth));
    }
    onClose();
  };

  const killStatusIcon = coin?.isEnemy ? STATUS_ICONS.Death : STATUS_ICONS['Bleeding Out'];
  return (
    <GGDialog 
      open={open} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <MenuHeader>
            <Typography>Modify Health</Typography>
            
              <Tooltip title={coin?.isEnemy ? 'Kill the character' : 'Down the character'}>
                <Button 
                  onClick={handleKill}
                  size="small"
                  startIcon={<Box
                        component="img"
                        src={ killStatusIcon }
                        sx={{
                          width: 16,
                          height: 16,
                          mb: .5,
                        }}
                      />}
                  sx={{ color: 'secondary.main' }}
                >
                  { coin?.isEnemy ? 'Kill' : 'Down' }
                </Button>
              </Tooltip>
            
          </MenuHeader>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <HealthStatCard 
              label="Current" 
              value={coin?.currentHealth} 
            />
            <HealthStatCard 
              label="Modified" 
              value={finalHealth} 
            />
             <HealthStatCard 
              label="Base" 
              value={coin?.maxHealth} 
            />
          </Box>
          <Divider>Modify Health</Divider>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Damage Amount */}
            <TextField
              label="Damage Amount"
              type="number"
              variant="outlined"
              value={damageAmount}
              onChange={(e) => handleHealthInputChange(e.target.value, setDamageAmount)}
              InputProps={{
                startAdornment: <InputAdornment position="start">-</InputAdornment>,
              }}
              fullWidth
            />
            {/* Heal Amount */}
            <TextField
              label="Heal Amount"
              type="number"
              variant="outlined"
              value={healAmount}
              onChange={(e) => handleHealthInputChange(e.target.value, setHealAmount)}
              InputProps={{
                startAdornment: <InputAdornment position="start">+</InputAdornment>,
              }}
              fullWidth
            />
            
          </Box>

          <Divider>Final Health</Divider>

          <TextField
            label="Modified Health"
            type="number"
            variant="outlined"
            fullWidth
            value={finalHealth}
            onChange={(e) => handleHealthInputChange(e.target.value, setFinalHealth)}
            inputProps={{
              min: 0
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="outlined"
          disabled={!finalHealth}
        >
          Save
        </Button>
      </DialogActions>
    </GGDialog>
  );
};

// Edit Status Dialog
const StatusCard = styled(Card)(({ theme, active }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  opacity: active ? 1 : 0.5,
  backgroundColor: active ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
  border: active ? `2px solid ${theme.palette.warning.main}` : '1px solid rgba(255, 255, 255, 0.12)',
  '&:hover': {
    opacity: 1,
    transform: 'scale(1.02)',
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
  },
  textAlign: 'center',
}));

export const EditStatusDialog = ({
  open,
  onClose,
  coin,
  onSave,
}) => {
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  useEffect(() => {
    if (coin) {
      setSelectedStatuses(coin.statuses || []);
    }
  }, [coin]);

  const handleStatusToggle = (status) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleSave = () => {
    onSave?.(selectedStatuses);
    onClose();
  };

  const allStatuses = Object.keys(STATUS_ICONS);
  const sortedStatuses = [
    ...allStatuses.filter(s => selectedStatuses.includes(s)),
    ...allStatuses.filter(s => !selectedStatuses.includes(s))
  ];

  return (
    <GGDialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <GGDialogTitle>Manage Statuses</GGDialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Grid container spacing={1.5}>
            {sortedStatuses.map((status) => {
              const isActive = selectedStatuses.includes(status);
              return (
                <Grid item xs={4} key={status}>
                  <StatusCard
                    active={isActive}
                    onClick={() => handleStatusToggle(status)}
                    variant="outlined"
                  >
                    <CardContent sx={{ 
                      py: 1.5, 
                      px: 2, 
                      '&:last-child': { pb: 1.5 },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}>
                      <Box
                        component="img"
                        src={STATUS_ICONS[status]}
                        alt={status}
                        sx={{
                          width: 48,
                          height: 48,
                          mb: 1,
                        }}
                      />
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          textTransform: 'uppercase', 
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          fontSize: '0.7rem'
                        }}
                      >
                        {status}
                      </Typography>
                    </CardContent>
                  </StatusCard>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="outlined"
        >
          Save
        </Button>
      </DialogActions>
    </GGDialog>
  );
};

